<template>
    <div class="key-dates">
        <div class="tab-header">
            <div class="tab-header-left">
                <h3 class="tab-title">Key Dates</h3>
            </div>
            <themed-button 
                v-if="hasLeases && leaseCount"
                @click="manageKeyDates"
                variant="default"
                aria-label="Manage key dates"
            >
                <theme-icon icon="edit-icon" type="icons" classes="w-4 h-4" />
                <span>Manage Key Dates</span>
            </themed-button>
        </div>
        
        <div v-if="isLoading" class="key-dates__loading">
            Loading key dates...
        </div>
        
        <div v-else-if="!hasLeases" class="key-dates__no-leases">
            <p class="none-found text-left">No leases found for this site. Please add a lease first.</p>
        </div>
        
        <div v-else-if="keyDates.length === 0" class="key-dates__empty">
            <p class="none-found text-left">No Key Dates Found</p>
        </div>
        
        <div v-else class="key-dates__content">
            <div v-for="leaseGroup in groupedKeyDates" :key="leaseGroup.lease.id" class="key-dates__lease-group">
                <div class="key-dates__lease-name">{{ leaseGroup.lease.name }}</div>
                <themed-table
                    :columns="tableColumns"
                    :data="leaseGroup.dates"
                    :pagination="false"
                    :searchable="false"
                    :sortable="false"
                    :hoverable="false"
                />
            </div>
        </div>

        <!-- Manage Key Dates Modal -->
    </div>
</template>

<script>
    import moment from 'moment';
    import ThemedButton from '../ThemedButton.vue';
    
    export default {
        components: {
            ThemedButton,
        },
        props: {
            siteId: {
                type: [Number, String],
                required: true
            }
        },

        data() {
            return {
                isLoading: true,
                leases: [],
                keyDates: [],
                tableColumns: [
                    {
                        field: 'lease_clause',
                        label: 'Clause',
                        type: 'text',
                        align: 'center',
                        width: '15%'
                    },
                    {
                        field: 'occurs_on',
                        label: 'Occurs On',
                        type: 'text',
                        align: 'center',
                        width: '15%',
                        formatter: (value) => this.formatDate(value)
                    },
                    {
                        field: 'type',
                        label: 'Type',
                        type: 'text',
                        width: '15%'
                    },
                    {
                        field: 'status',
                        label: 'Status',
                        type: 'text',
                        align: 'center',
                        width: '15%',
                        formatter: (value, row) => {
                            const variant = row.completed_at ? 'success' : 'destructive';
                            const statusText = row.completed_at ? 'Completed' : 'Incomplete';
                            const badgeClass = variant === 'success' ? 'themed-badge--success' : 'themed-badge--destructive';
                            return `<span class="themed-badge ${badgeClass}">${statusText}</span>`;
                        }
                    },
                    {
                        field: 'note',
                        label: 'Note',
                        type: 'text',
                        width: '40%',
                        formatter: (value, row) => {
                            if (value) {
                                return `<span class="key-dates__note" title="${value}">${value}</span>`;
                            }
                            return '<span class="key-dates__no-note">-</span>';
                        }
                    }
                ]
            }
        },

        mounted() {
            this.loadData();
            
            // Listen for lease changes
            this.$eventHub.$on('lease-added', this.loadData);
            this.$eventHub.$on('lease-updated', this.loadData);
            this.$eventHub.$on('lease-deleted', this.loadData);
        },

        beforeDestroy() {
            this.$eventHub.$off('lease-added', this.loadData);
            this.$eventHub.$off('lease-updated', this.loadData);
            this.$eventHub.$off('lease-deleted', this.loadData);
        },

        computed: {
            hasLeases() {
                return this.leases.length > 0;
            },
            
            leaseCount() {
                return this.leases.length;
            },
            
            groupedKeyDates() {
                const grouped = {};
                
                this.keyDates.forEach(date => {
                    if (!grouped[date.lease_id]) {
                        const lease = this.leases.find(l => l.id === date.lease_id);
                        grouped[date.lease_id] = {
                            lease: lease || { id: date.lease_id, name: 'Unknown Lease' },
                            dates: []
                        };
                    }
                    grouped[date.lease_id].dates.push(date);
                });
                
                return Object.values(grouped);
            },

        },

        methods: {
            async loadData() {
                this.isLoading = true;
                
                try {
                    const response = await axios.get(`/api/site/${this.siteId}`);
                    const site = response.data;
                    
                    this.leases = site.leases || [];
                    
                    // Flatten key dates from all leases
                    this.keyDates = this.leases.flatMap(lease => 
                        (lease.key_dates || []).map(date => ({
                            ...date,
                            lease_id: lease.id
                        }))
                    ).sort((a, b) => new Date(a.occurs_on) - new Date(b.occurs_on));
                    
                } catch (error) {
                    console.error('Failed to load key dates:', error);
                    this.$toast.error('Failed to load key dates');
                } finally {
                    this.isLoading = false;
                }
            },

            manageKeyDates() {
                this.$emit('open-manage-leases', 'key-dates');
            },


            
            formatDate(date) {
                return moment(date).format('MM/DD/YYYY');
            },

        }
    }
</script>
